/**
 * ───────────────────────────────────────────────────────────────────────────
 * /pwa-check — ON-DEVICE PWA INSTALL DIAGNOSTICS
 * ───────────────────────────────────────────────────────────────────────────
 *
 * WHY THIS EXISTS
 * The install prompt did not appear on a physical Android phone. Every
 * prerequisite can be verified from a laptop EXCEPT the ones that only exist
 * on the device itself: whether Chrome fired `beforeinstallprompt`, whether
 * the service worker actually activated, what localStorage already contains,
 * and whether the page is running inside an iframe or already installed.
 *
 * This page runs those checks IN the browser being tested and prints a
 * copy-pasteable report. Open it on the Android phone, tap "Copy report",
 * paste it back — that is real device evidence, not a code review.
 *
 * It is noindex + unlinked (internal tool), and it reads nothing but browser
 * APIs: no account data, no PII, no credentials.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  decidePrompt,
  forcePrompt,
  hasInstallEvent,
  installEventFired,
  isIos,
  isStandalone,
  onPwaChange,
  platform,
  pwaLogEntries,
  readPwaState,
  reasonText,
  resetPwaState,
  dismissWindowDays,
} from '@/lib/pwa';

type Verdict = 'pass' | 'fail' | 'warn' | 'info';

interface Check {
  id: string;
  question: string;
  verdict: Verdict;
  answer: string;
  detail?: string;
}

const dot: Record<Verdict, string> = {
  pass: 'bg-emerald-500',
  fail: 'bg-red-500',
  warn: 'bg-amber-500',
  info: 'bg-slate-400',
};

const label: Record<Verdict, string> = {
  pass: 'PASS',
  fail: 'FAIL',
  warn: 'CHECK',
  info: 'INFO',
};

export default function PwaCheck() {
  const [checks, setChecks] = useState<Check[]>([]);
  const [logs, setLogs] = useState(() => pwaLogEntries());
  const [busy, setBusy] = useState(true);
  const [copied, setCopied] = useState(false);

  /** Runs every check that can only be answered by this browser, right now. */
  const run = useCallback(async () => {
    setBusy(true);
    const out: Check[] = [];
    const state = readPwaState();
    const decision = decidePrompt();
    const inIframe = window.top !== window.self;

    /* 8. Is the device/browser eligible at all? ---------------------------- */
    const ua = navigator.userAgent || '';
    const chromium = /Chrome|Chromium|CriOS|Edg/i.test(ua) && !/OPR|SamsungBrowser/i.test(ua);
    out.push({
      id: 'eligibility',
      question: '8. Is this device / browser eligible for installation?',
      verdict: isIos() ? 'warn' : chromium ? 'pass' : 'warn',
      answer: isIos()
        ? 'iOS — no programmatic install exists. Add to Home Screen is manual via the Share sheet.'
        : chromium
          ? 'Chromium-based browser detected — beforeinstallprompt is supported.'
          : 'Not a Chromium browser. Firefox and Safari never fire beforeinstallprompt.',
      detail: `platform=${platform()} · UA=${ua.slice(0, 160)}`,
    });

    /* Secure context + iframe (the two silent killers) --------------------- */
    out.push({
      id: 'context',
      question: 'Is the page a top-level, secure context?',
      verdict: window.isSecureContext && !inIframe ? 'pass' : 'fail',
      answer: !window.isSecureContext
        ? 'NOT a secure context — install is impossible without HTTPS.'
        : inIframe
          ? 'Running inside an IFRAME. Chrome never fires beforeinstallprompt in an iframe — test the site in a real Chrome tab, not inside a preview/editor frame.'
          : 'Top-level HTTPS page.',
      detail: `secureContext=${window.isSecureContext} · inIframe=${inIframe} · origin=${window.location.origin}`,
    });

    /* 7. Already installed? ----------------------------------------------- */
    let relatedApps = 'getInstalledRelatedApps() unsupported';
    try {
      const nav = navigator as unknown as { getInstalledRelatedApps?: () => Promise<unknown[]> };
      if (typeof nav.getInstalledRelatedApps === 'function') {
        const apps = await nav.getInstalledRelatedApps();
        relatedApps = `getInstalledRelatedApps() returned ${apps.length} app(s)`;
      }
    } catch {
      relatedApps = 'getInstalledRelatedApps() threw';
    }
    const standalone = isStandalone();
    out.push({
      id: 'installed',
      question: '7. Is the browser already treating the site as installed?',
      verdict: standalone || state.installed ? 'warn' : 'pass',
      answer: standalone
        ? 'YES — this page is already running in standalone mode, so Chrome will not offer to install it again.'
        : state.installed
          ? 'Our own flag says installed. Chrome may or may not agree — tap Reset below and retest.'
          : 'No: not standalone and no stored install flag.',
      detail: `display-mode standalone=${standalone} · gg_pwa.installed=${!!state.installed} · ${relatedApps}`,
    });

    /* 4. Manifest --------------------------------------------------------- */
    try {
      const res = await fetch('/manifest.webmanifest', { cache: 'no-store' });
      const ct = res.headers.get('content-type') || '(none)';
      const body = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(body) as Record<string, unknown>;
      } catch {
        parsed = null;
      }
      const icons = (parsed?.icons as { sizes?: string; purpose?: string }[] | undefined) || [];
      const has192 = icons.some((i) => (i.sizes || '').includes('192'));
      const has512 = icons.some((i) => (i.sizes || '').includes('512'));
      const ok = res.ok && !!parsed && has192 && has512 && parsed.display === 'standalone';
      out.push({
        id: 'manifest',
        question: '4. Is manifest.webmanifest loading successfully?',
        verdict: ok ? 'pass' : 'fail',
        answer: !res.ok
          ? `HTTP ${res.status} — the manifest is not being served.`
          : !parsed
            ? 'Fetched, but the body is NOT valid JSON (a SPA HTML fallback is the usual cause). Chrome discards it and never fires beforeinstallprompt.'
            : `Valid JSON. name="${String(parsed.name || '')}" display=${String(parsed.display)} 192px=${has192} 512px=${has512}`,
        detail: `status=${res.status} · content-type=${ct} · bytes=${body.length} · icons=${icons.length}`,
      });

      // Icon reachability — a manifest icon that 404s fails installability.
      const firstIcon = (parsed?.icons as { src?: string }[] | undefined)?.[0]?.src;
      if (firstIcon) {
        let iconNote = 'not checked';
        let iconOk: Verdict = 'info';
        try {
          const ir = await fetch(firstIcon, { cache: 'no-store', mode: 'cors' });
          iconNote = `HTTP ${ir.status} · ${ir.headers.get('content-type') || 'unknown type'}`;
          iconOk = ir.ok ? 'pass' : 'fail';
        } catch (e) {
          iconNote = `fetch blocked/failed: ${(e as Error).message}. Chrome may still load it (manifest icons are not CORS-restricted), so treat this as inconclusive.`;
          iconOk = 'warn';
        }
        out.push({
          id: 'icon',
          question: 'Is the 192px manifest icon reachable from this device?',
          verdict: iconOk,
          answer: iconNote,
          detail: firstIcon,
        });
      }
    } catch (e) {
      out.push({
        id: 'manifest',
        question: '4. Is manifest.webmanifest loading successfully?',
        verdict: 'fail',
        answer: `Fetch threw: ${(e as Error).message}`,
      });
    }

    /* 3. Service worker --------------------------------------------------- */
    if (!('serviceWorker' in navigator)) {
      out.push({
        id: 'sw',
        question: '3. Is the service worker registered and activated?',
        verdict: 'fail',
        answer: 'serviceWorker is not available in this browser context (private mode or disabled).',
      });
    } else {
      const regs = await navigator.serviceWorker.getRegistrations();
      const reg = regs.find((r) => r.active || r.installing || r.waiting);
      const activeState = reg?.active?.state || reg?.waiting?.state || reg?.installing?.state || 'none';
      out.push({
        id: 'sw',
        question: '3. Is the service worker registered and activated?',
        verdict: reg?.active ? 'pass' : reg ? 'warn' : 'fail',
        answer: reg?.active
          ? `Registered and ACTIVE (scope ${reg.scope}).`
          : reg
            ? `Registered but not active yet — state "${activeState}". Reload once.`
            : 'NOT registered. Chrome requires an active service worker with a fetch handler before it will offer an install.',
        detail: `registrations=${regs.length} · controller=${navigator.serviceWorker.controller ? 'yes' : 'no (first load — normal)'} · state=${activeState}`,
      });
    }

    /* 2. beforeinstallprompt ---------------------------------------------- */
    out.push({
      id: 'bip',
      question: '2. Is beforeinstallprompt firing?',
      verdict: installEventFired() ? 'pass' : isIos() ? 'info' : 'fail',
      answer: installEventFired()
        ? `YES — captured${hasInstallEvent() ? ' and still usable' : ' but already consumed by a prompt() call'}.`
        : isIos()
          ? 'Not applicable on iOS — Safari never fires this event.'
          : 'NOT fired on this page view. Chrome fires it only after the manifest + active SW criteria pass, and only in a top-level tab. Check the rows above, then hard-reload.',
      detail: `capturedThisView=${installEventFired()} · eventStillAvailable=${hasInstallEvent()}`,
    });

    /* 1. Chrome menu ------------------------------------------------------ */
    out.push({
      id: 'menu',
      question: '1. Does Chrome show "Install app" in the browser menu?',
      verdict: 'info',
      answer:
        'Only you can confirm this: Chrome ⋮ menu → look for "Install app" / "Add to Home screen". If every row above passes but the menu entry is missing, the failure is on Chrome\'s side (usually a stale SW — clear site data and retry).',
    });

    /* 5 + 6. Our own suppression rules ------------------------------------ */
    out.push({
      id: 'visits',
      question: '5. Is our second-visit rule suppressing the prompt?',
      verdict: decision.reason === 'no-trigger' ? 'fail' : 'pass',
      answer:
        decision.reason === 'no-trigger'
          ? `YES — this browser has ${state.visits} counted session(s) and needs 2. Close the tab, reopen the site, or tap "Force show" below.`
          : `No — ${state.visits} counted session(s), trigger "${decision.trigger || 'none'}".`,
      detail: `gg_pwa.visits=${state.visits} (one per browsing session, not per reload)`,
    });

    out.push({
      id: 'storage',
      question: '6. Is localStorage incorrectly marking the prompt as dismissed?',
      verdict: state.dismissedAt || state.installed ? 'warn' : 'pass',
      answer: state.dismissedAt
        ? `A dismissal is stored at ${state.dismissedAt} — suppressed for ${dismissWindowDays} days from then.`
        : state.installed
          ? 'An install flag is stored, which suppresses the prompt permanently.'
          : 'No: nothing stored that would suppress the prompt.',
      detail: `gg_pwa=${JSON.stringify(state)}`,
    });

    /* Final verdict ------------------------------------------------------- */
    out.push({
      id: 'decision',
      question: 'What is the app deciding right now?',
      verdict: decision.show ? 'pass' : 'warn',
      answer: reasonText(decision.reason),
      detail: `show=${decision.show} · mode=${decision.mode} · trigger=${decision.trigger || 'none'} · reason=${decision.reason || 'eligible'}`,
    });

    setChecks(out);
    setLogs(pwaLogEntries());
    setBusy(false);
  }, []);

  useEffect(() => {
    document.title = 'PWA install diagnostics — GIGGONE';
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex,nofollow';
    document.head.appendChild(meta);
    run();
    const off = onPwaChange(() => run());
    return () => {
      off();
      meta.remove();
    };
  }, [run]);

  const report = () =>
    [
      `GIGGONE PWA diagnostics — ${new Date().toISOString()}`,
      `URL: ${window.location.href}`,
      `UA: ${navigator.userAgent}`,
      '',
      ...checks.map((c) => `[${label[c.verdict]}] ${c.question}\n    ${c.answer}${c.detail ? `\n    ${c.detail}` : ''}`),
      '',
      'EVENT LOG',
      ...logs.map((l) => `  ${l.t} ${l.name} ${l.detail ? JSON.stringify(l.detail) : ''}`),
    ].join('\n');

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(report());
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      window.prompt('Copy this report:', report());
    }
  };

  const failures = checks.filter((c) => c.verdict === 'fail').length;

  return (
    <main className="mx-auto max-w-3xl px-4 py-10">
      <h1 className="text-2xl font-bold text-slate-900">PWA install diagnostics</h1>
      <p className="mt-2 text-sm text-slate-600">
        Run this page <strong>on the device that is failing</strong> (Android Chrome, in a normal tab — not
        inside an editor preview). Every row below is measured live by this browser. Tap{' '}
        <strong>Copy report</strong> and paste the result back for review.
      </p>

      <div
        className={`mt-6 rounded-lg border p-4 text-sm ${
          failures ? 'border-red-200 bg-red-50 text-red-900' : 'border-emerald-200 bg-emerald-50 text-emerald-900'
        }`}
      >
        {busy
          ? 'Running checks…'
          : failures
            ? `${failures} blocking problem(s) found on this device. The first FAIL row is the cause.`
            : 'No blocking problems detected on this device.'}
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        <button
          onClick={run}
          className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          Re-run checks
        </button>
        <button
          onClick={() => {
            (window as unknown as { __ggPwaReopen?: () => void }).__ggPwaReopen?.();
            forcePrompt();
            run();
          }}
          className="rounded-lg bg-orange-500 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-600"
        >
          Force show prompt
        </button>

        <button
          onClick={() => {
            resetPwaState();
            run();
          }}
          className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          Reset stored state
        </button>
        <button
          onClick={copy}
          className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
        >
          {copied ? 'Copied' : 'Copy report'}
        </button>
      </div>

      <ul className="mt-8 space-y-3">
        {checks.map((c) => (
          <li key={c.id} className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="flex items-start gap-3">
              <span className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${dot[c.verdict]}`} aria-hidden="true" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-900">
                  <span className="mr-2 text-xs font-bold tracking-wide text-slate-500">{label[c.verdict]}</span>
                  {c.question}
                </p>
                <p className="mt-1 text-sm text-slate-700">{c.answer}</p>
                {c.detail && (
                  <p className="mt-1 break-words font-mono text-xs text-slate-500">{c.detail}</p>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>

      <h2 className="mt-10 text-lg font-semibold text-slate-900">Event log (this page view)</h2>
      <p className="mt-1 text-sm text-slate-600">
        The same lines are printed to the console with the prefix <code>[GIGGONE PWA]</code>.
      </p>
      <pre className="mt-3 max-h-72 overflow-auto rounded-lg bg-slate-900 p-4 text-xs leading-relaxed text-slate-100">
        {logs.length
          ? logs.map((l) => `${l.t}  ${l.name}${l.detail ? `  ${JSON.stringify(l.detail)}` : ''}`).join('\n')
          : 'No events recorded yet.'}
      </pre>

      <p className="mt-6 text-xs text-slate-500">
        Console helpers on this device: <code>__ggPwaDebug.status()</code>, <code>__ggPwaDebug.force()</code>,{' '}
        <code>__ggPwaDebug.reset()</code>, <code>__ggPwaDebug.log()</code>.
      </p>
    </main>
  );
}
